import { navigate } from 'gatsby';
import type { MUIDataTableOptions } from 'mui-datatables';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import RoundedButton from '@components/RoundedButton';

import * as ROUTES from '@constants/routes';

import { formatDate } from '@utils/format';

import { useAdminListRoles } from 'generated/admin.generated';

const messages = defineMessages({
  name: {
    id: 'admin.roles.name',
    defaultMessage: 'Name',
  },
  description: {
    id: 'admin.roles.description',
    defaultMessage: 'Description',
  },
  updatedAt: {
    id: 'GLOBAL.updatedAt',
    defaultMessage: 'Updated at',
  },
  createdAt: {
    id: 'GLOBAL.createdAt',
    defaultMessage: 'Created At',
  },
  noData: {
    id: 'admin.roles.noData',
    defaultMessage: 'No roles',
  },
  create: {
    id: 'admin.roles.create',
    defaultMessage: 'New Role',
  },
});

interface RowData {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

function RolesList() {
  const { formatMessage } = useIntl();
  const { data, loading } = useAdminListRoles();

  const tableData = React.useMemo(
    () =>
      (data?.listRoles ?? []).map<RowData>((role) => {
        return {
          id: role.role_id,
          name: role.role_name,
          description: role.role_description,
          updated_at: role.updated_at,
          created_at: role.created_at,
        };
      }, []),
    [data]
  );

  const handleRowClick: MUIDataTableOptions['onRowClick'] = (
    _rowData,
    { dataIndex }
  ) => {
    const { id } = tableData[dataIndex];
    navigate(`${ROUTES.ADMIN_MANAGE_ROLES}${id}`);
  };

  const handleCreate = () => {
    navigate(ROUTES.ADMIN_MANAGE_ROLES_CREATE);
  };

  return (
    <ContentLoader loading={loading}>
      <MuiDataTable
        columns={[
          {
            name: 'name',
            className: 'blue-hover',
            label: formatMessage(messages.name),
            options: {
              searchable: true,
              sort: true,
            },
          },
          {
            name: 'description',
            className: 'small-light',
            label: formatMessage(messages.description),
            options: {
              searchable: true,
              sort: true,
            },
          },
          {
            name: 'created_at',
            className: 'date',
            label: formatMessage(messages.createdAt),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['created_at']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
          },
          {
            name: 'updated_at',
            className: 'date',
            label: formatMessage(messages.updatedAt),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (value: RowData['updated_at']) =>
                value
                  ? formatDate({ date: value, formatter: 'datetime' })
                  : '-',
            },
          },
        ]}
        data={tableData}
        options={{
          search: true,
          sort: true,
          filter: true,
          sortOrder: {
            name: 'name',
            direction: 'asc',
          },
          onRowClick: handleRowClick,
          textLabels: {
            body: {
              noMatch: formatMessage(messages.noData),
            },
          },
          customToolbar: () => (
            <RoundedButton
              autoSize
              iconName="plus"
              margin={0}
              onClick={handleCreate}
            >
              {formatMessage(messages.create)}
            </RoundedButton>
          ),
        }}
      />
    </ContentLoader>
  );
}

export default RolesList;
